import type {
	OpaqueData,
	PaymentGatewayConfig,
	PaymentMethodFormData,
} from '../../types/payment.types';
import {
	PaymentGatewayError,
	PaymentMethodTypeEnum,
} from '../../types/payment.types';
import { ApiService } from '../ApiService';

// ============================================================================
// Collect.JS Types
// ============================================================================

/**
 * Collect.JS callback response containing the one-time payment token
 * and masked payment information.
 */
interface CollectJSResponse {
	token: string;
	card?: {
		number: string; // Masked card number, e.g. "411111******1111"
		bin: string;
		exp: string; // e.g. "1025"
		hash: string;
		type: string; // e.g. "visa", "mastercard" — the BRAND
		/**
		 * PD-15142 — how the card is FUNDED: 'credit', 'debit', 'prepaid',
		 * 'charge', 'deferred_debit' or 'unknown'. Distinct from `type`, which is
		 * only the brand and says nothing about funding.
		 *
		 * This decides whether a processing fee may be charged at all:
		 * Regulation II prohibits surcharging debit, including signature debit
		 * run as credit. Collect.JS resolves it by BIN lookup while the customer
		 * is still on the form, so capturing it here costs nothing and saves a
		 * server-side lookup on first use.
		 */
		category?: string;
	};
	check?: {
		name: string;
		account: string; // Masked account, e.g. "****1234"
		aba: string; // Routing number
		hash: string;
	};
}

interface CollectJSFieldsAvailableEvent {
	[fieldName: string]: boolean;
}

interface CollectJSConfigOptions {
	variant?: 'inline';
	styleSniffer?: boolean;
	callback: (response: CollectJSResponse) => void;
	validationCallback?: (field: string, valid: boolean, message: string) => void;
	fieldsAvailableCallback?: (fields: CollectJSFieldsAvailableEvent) => void;
	timeoutCallback?: () => void;
	timeoutDuration?: number;
	fields?: Record<
		string,
		{
			selector: string;
			title?: string;
			placeholder?: string;
		}
	>;
	customCss?: Record<string, string>;
	focusCss?: Record<string, string>;
	invalidCss?: Record<string, string>;
	placeholderCss?: Record<string, string>;
	validCss?: Record<string, string>;
}

/**
 * Global CollectJS interface (loaded dynamically via Collect.js script)
 */
interface CollectJSGlobal {
	configure: (options: CollectJSConfigOptions) => void;
	startPaymentRequest: () => void;
}

declare global {
	interface Window {
		CollectJS?: CollectJSGlobal;
	}
}

type PaymentContextType = 'CARD' | 'CHECK';

// ============================================================================
// NMI FRONTEND SERVICE
// ============================================================================

export class NmiFrontendService {
	private _config: PaymentGatewayConfig | null = null;
	private _scriptLoaded = false;

	// Card-specific state
	private _isCardInitialized = false;
	private _detectedCardBrand: string | null = null;

	// ACH/Check-specific state
	private _isCheckInitialized = false;

	// Promise-based callback resolution for tokenization.
	// Collect.JS uses a global callback model, so we store resolve/reject
	// references that the callback invokes when tokenization completes.
	private _tokenResolve: ((value: OpaqueData) => void) | null = null;
	private _tokenReject: ((reason: Error) => void) | null = null;
	private _currentPaymentType: PaymentContextType | null = null;

	// Hosted-field validity tracked from Collect.JS validation events.
	// Collect.JS silently refuses to generate a token while any required
	// hosted field is invalid — without tracking this, a submit with a bad
	// field just hangs until the 30s timeout fires with a generic
	// "timed out" message. We use this map to fail fast with a message
	// naming the offending field instead.
	private readonly _fieldValidity = new Map<
		string,
		{ valid: boolean; message: string }
	>();
	private _tokenRequestInFlight = false;

	// Maps Collect.JS field keys to the user-facing labels used in the form
	private static readonly _fieldLabels: Record<string, string> = {
		ccnumber: 'Card Number',
		ccexp: 'Expiration Date',
		cvv: 'Security Code',
		checkaba: 'Routing Number',
		checkaccount: 'Account Number',
		checkname: 'Name on Account',
	};

	// The Collect.JS fields that must be valid per payment context
	private static readonly _contextFields: Record<
		PaymentContextType,
		Array<string>
	> = {
		CARD: ['ccnumber', 'ccexp', 'cvv'],
		CHECK: ['checkaba', 'checkaccount', 'checkname'],
	};

	// Card brand callback from validation events
	private _onCardBrandChange: ((brand: string | null) => void) | null = null;

	private static readonly PRODUCTION_SCRIPT_URL =
		'https://secure.nmi.com/token/Collect.js';
	private static readonly SANDBOX_SCRIPT_URL =
		'https://sandbox.nmi.com/token/Collect.js';

	// Static flag to track if preloading has been started
	private static _preloadStarted = false;

	private static _getScriptUrl(environment?: string): string {
		return environment === 'production'
			? NmiFrontendService.PRODUCTION_SCRIPT_URL
			: NmiFrontendService.SANDBOX_SCRIPT_URL;
	}

	/**
	 * Preload the Collect.JS script for faster initialization later.
	 * This can be called early (e.g., when we know NMI is the processor)
	 * to reduce the delay when the user opens the payment form.
	 */
	public static preloadScript(
		tokenizationKey: string,
		environment?: string,
	): void {
		if (NmiFrontendService._preloadStarted) {
			return;
		}

		NmiFrontendService._preloadStarted = true;

		const scriptUrl = NmiFrontendService._getScriptUrl(environment);

		// Check if script already exists
		const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);

		if (existingScript != null) {
			return;
		}

		// Add preload link hint for the browser
		const preloadLink = document.createElement('link');
		preloadLink.rel = 'preload';
		preloadLink.as = 'script';
		preloadLink.href = scriptUrl;
		document.head.appendChild(preloadLink);

		// Load the script with the tokenization key attribute
		const script = document.createElement('script');
		script.src = scriptUrl;
		script.setAttribute('data-tokenization-key', tokenizationKey);
		script.async = true;
		document.head.appendChild(script);
	}

	/**
	 * Initialize the service by loading configuration and Collect.JS SDK
	 * @param paymentType - Type of payment to initialize: 'CARD' for credit cards, 'CHECK' for ACH/eCheck
	 * @param externalConfig Optional pre-fetched gateway config. When provided,
	 *   skips the internal ApiService call — allows non-CRM consumers (e.g. portal)
	 *   to supply config fetched through their own API layer.
	 */
	public async initialize(
		paymentType: PaymentContextType = 'CARD',
		externalConfig?: PaymentGatewayConfig,
	): Promise<void> {
		// Check if already initialized for this payment type
		if (paymentType === 'CARD' && this._isCardInitialized) {
			return;
		}

		if (paymentType === 'CHECK' && this._isCheckInitialized) {
			return;
		}

		try {
			// 1. Get configuration — use external config if provided, otherwise fetch from backend
			if (this._config == null) {
				this._config = externalConfig ?? (await this._getConfiguration());
			}

			// Only initialize if NMI is the processor
			if (this._config.processorType !== 'nmi') {
				throw new PaymentGatewayError(
					'NMI is not the configured payment processor',
					'WRONG_PROCESSOR',
				);
			}

			const tokenizationKey = this._config.publicCredentials.tokenizationKey;

			if (tokenizationKey == null || tokenizationKey === '') {
				throw new PaymentGatewayError(
					'NMI tokenization key not configured',
					'CONFIG_ERROR',
				);
			}

			// 2. Load Collect.JS script (use sandbox URL for sandbox environment)
			await this._loadCollectJsScript(
				tokenizationKey,
				this._config.environment,
			);

			// 3. Mark as initialized for this payment type
			if (paymentType === 'CARD') {
				this._isCardInitialized = true;
			} else {
				this._isCheckInitialized = true;
			}
		} catch (error) {
			let errorMessage = 'Failed to initialize NMI service';

			if (error instanceof PaymentGatewayError) {
				errorMessage = error.message;
			} else if (error instanceof Error) {
				errorMessage = error.message;
			}

			throw new PaymentGatewayError(errorMessage, 'INIT_ERROR', error);
		}
	}

	/**
	 * Create and mount the secure card fields using Collect.JS inline variant.
	 * NMI needs 3 containers: card number, expiration, and CVV.
	 *
	 * @param numberContainerId - ID of the container element for the card number field
	 * @param expContainerId - ID of the container element for the expiration field
	 * @param cvvContainerId - ID of the container element for the CVV field
	 * @param options - Optional configuration for styling and callbacks
	 */
	public createSecureCardFields(
		numberContainerId: string,
		expContainerId: string,
		cvvContainerId: string,
		options?: {
			onCardBrandChange?: (brand: string | null) => void;
			textColor?: string;
			placeholderColor?: string;
		},
	): void {
		if (!this._isCardInitialized || window.CollectJS == null) {
			throw new PaymentGatewayError(
				'Card service not initialized. Call initialize("CARD") first.',
				'NOT_INITIALIZED',
			);
		}

		this._onCardBrandChange = options?.onCardBrandChange ?? null;
		this._currentPaymentType = 'CARD';

		// Fresh field set — discard validity state from any prior configuration
		this._fieldValidity.clear();

		// Clear loading placeholders from containers before Collect.JS injects iframes.
		// Collect.JS appends iframes without clearing existing content.
		this._clearContainerContent(numberContainerId);
		this._clearContainerContent(expContainerId);
		this._clearContainerContent(cvvContainerId);

		const textColor = options?.textColor ?? '#333';
		const placeholderColor = options?.placeholderColor ?? '#9CA3AF';

		window.CollectJS.configure({
			variant: 'inline',
			styleSniffer: true,
			callback: (response: CollectJSResponse) => {
				this._handleTokenResponse(response);
			},
			validationCallback: (field: string, valid: boolean, message: string) => {
				this._handleValidation(field, valid, message);
			},
			fieldsAvailableCallback: () => {
				// Stretch the injected iframes to fill their containers
				this._stretchIframes(numberContainerId);
				this._stretchIframes(expContainerId);
				this._stretchIframes(cvvContainerId);
			},
			timeoutDuration: 30000,
			timeoutCallback: () => {
				this._handleTokenTimeout();
			},
			fields: {
				ccnumber: {
					selector: `#${numberContainerId}`,
					title: 'Card Number',
					placeholder: 'Card Number',
				},
				ccexp: {
					selector: `#${expContainerId}`,
					title: 'Expiration Date',
					placeholder: 'MM/YY',
				},
				cvv: {
					selector: `#${cvvContainerId}`,
					title: 'CVV',
					placeholder: 'CVV',
				},
			},
			customCss: {
				color: textColor,
				'font-size': '14px',
				'font-family':
					'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
				height: '100%',
				width: '100%',
				// Padding inside the iframe input matches Tailwind px-3 py-2.
				// The outer container has no padding so the iframe fills it
				// entirely, ensuring any click within the border triggers focus.
				padding: '8px 12px',
				margin: '0',
				border: 'none',
				background: 'transparent',
				'box-shadow': 'none',
				outline: 'none',
				'box-sizing': 'border-box',
			},
			placeholderCss: {
				color: placeholderColor,
			},
			focusCss: {
				outline: 'none',
				border: 'none',
				'box-shadow': 'none',
			},
			invalidCss: {
				color: textColor,
			},
			validCss: {
				color: textColor,
			},
		});
	}

	/**
	 * Create and mount the secure ACH/eCheck fields using Collect.JS inline variant.
	 * NMI requires all three check fields (routing, account, AND name on account)
	 * to be hosted — a token is never generated when checkname is absent, and the
	 * request just times out.
	 *
	 * @param routingContainerId - ID of the container element for the routing number field
	 * @param accountContainerId - ID of the container element for the account number field
	 * @param nameContainerId - ID of the container element for the name-on-account field
	 * @param options - Optional configuration for styling
	 */
	public createSecureCheckFields(
		routingContainerId: string,
		accountContainerId: string,
		nameContainerId: string,
		options?: {
			textColor?: string;
			placeholderColor?: string;
		},
	): void {
		if (!this._isCheckInitialized || window.CollectJS == null) {
			throw new PaymentGatewayError(
				'Check service not initialized. Call initialize("CHECK") first.',
				'NOT_INITIALIZED',
			);
		}

		this._currentPaymentType = 'CHECK';

		// Fresh field set — discard validity state from any prior configuration
		this._fieldValidity.clear();

		// Clear loading placeholders from containers before Collect.JS injects iframes
		this._clearContainerContent(routingContainerId);
		this._clearContainerContent(accountContainerId);
		this._clearContainerContent(nameContainerId);

		const textColor = options?.textColor ?? '#333';
		const placeholderColor = options?.placeholderColor ?? '#9CA3AF';

		window.CollectJS.configure({
			variant: 'inline',
			styleSniffer: true,
			callback: (response: CollectJSResponse) => {
				this._handleTokenResponse(response);
			},
			validationCallback: (field: string, valid: boolean, message: string) => {
				this._handleValidation(field, valid, message);
			},
			fieldsAvailableCallback: () => {
				// Stretch the injected iframes to fill their containers
				this._stretchIframes(routingContainerId);
				this._stretchIframes(accountContainerId);
				this._stretchIframes(nameContainerId);
			},
			timeoutDuration: 30000,
			timeoutCallback: () => {
				this._handleTokenTimeout();
			},
			fields: {
				checkaba: {
					selector: `#${routingContainerId}`,
					title: 'Routing Number',
					placeholder: 'Routing Number',
				},
				checkaccount: {
					selector: `#${accountContainerId}`,
					title: 'Account Number',
					placeholder: 'Account Number',
				},
				checkname: {
					selector: `#${nameContainerId}`,
					title: 'Name on Account',
					placeholder: 'Full name as shown on account',
				},
			},
			customCss: {
				color: textColor,
				'font-size': '14px',
				'font-family':
					'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
				height: '100%',
				width: '100%',
				padding: '8px 12px',
				margin: '0',
				border: 'none',
				background: 'transparent',
				'box-shadow': 'none',
				outline: 'none',
				'box-sizing': 'border-box',
			},
			placeholderCss: {
				color: placeholderColor,
			},
			focusCss: {
				outline: 'none',
				border: 'none',
				'box-shadow': 'none',
			},
			invalidCss: {
				color: textColor,
			},
			validCss: {
				color: textColor,
			},
		});
	}

	/**
	 * Tokenize credit card data using Collect.JS.
	 * The card number, expiration, and CVV are in secure fields.
	 */
	public async tokenizeCardData(
		_formData: PaymentMethodFormData,
	): Promise<OpaqueData> {
		if (!this._isCardInitialized || window.CollectJS == null) {
			throw new PaymentGatewayError(
				'Card service not initialized. Call initialize("CARD") first.',
				'NOT_INITIALIZED',
			);
		}

		this._currentPaymentType = 'CARD';

		return this._startTokenRequest('CARD');
	}

	/**
	 * Tokenize eCheck/ACH bank account data using Collect.JS.
	 * The routing number, account number, and name on account are in secure fields.
	 */
	public async tokenizeBankData(
		_formData: PaymentMethodFormData,
	): Promise<OpaqueData> {
		if (!this._isCheckInitialized || window.CollectJS == null) {
			throw new PaymentGatewayError(
				'Check service not initialized. Call initialize("CHECK") first.',
				'NOT_INITIALIZED',
			);
		}

		this._currentPaymentType = 'CHECK';

		return this._startTokenRequest('CHECK');
	}

	/**
	 * Kick off a Collect.JS tokenization request.
	 * Fails fast when a hosted field is already known to be invalid —
	 * Collect.JS will not generate a token in that state and would otherwise
	 * leave the user waiting for the 30s timeout.
	 */
	private _startTokenRequest(
		paymentType: PaymentContextType,
	): Promise<OpaqueData> {
		return new Promise<OpaqueData>((resolve, reject) => {
			const invalidLabels = this._getKnownInvalidFieldLabels(paymentType);

			if (invalidLabels.length > 0) {
				reject(
					new PaymentGatewayError(
						this._buildInvalidFieldsMessage(invalidLabels),
						'VALIDATION_ERROR',
					),
				);
				return;
			}

			this._tokenResolve = resolve;
			this._tokenReject = reject;
			this._tokenRequestInFlight = true;

			// Trigger Collect.JS tokenization — the response comes via the callback
			window.CollectJS?.startPaymentRequest();
		});
	}

	/**
	 * Labels of the current context's hosted fields that Collect.JS has
	 * reported as invalid (fields it has not validated yet are not included).
	 */
	private _getKnownInvalidFieldLabels(
		paymentType: PaymentContextType,
	): Array<string> {
		return NmiFrontendService._contextFields[paymentType]
			.filter((field) => this._fieldValidity.get(field)?.valid === false)
			.map((field) => NmiFrontendService._fieldLabels[field] ?? field);
	}

	private _buildInvalidFieldsMessage(invalidLabels: Array<string>): string {
		const verb = invalidLabels.length === 1 ? 'is' : 'are';
		return `${invalidLabels.join(', ')} ${verb} missing or invalid. Please check and try again.`;
	}

	/**
	 * Get the detected card brand from the secure field
	 */
	public getDetectedCardBrand(): string | null {
		return this._detectedCardBrand;
	}

	/**
	 * Get masked card info from the last tokenization response.
	 * This is stored during the Collect.JS callback.
	 */
	private _lastTokenResponse: CollectJSResponse | null = null;

	public getLastTokenResponse(): CollectJSResponse | null {
		return this._lastTokenResponse;
	}

	/**
	 * Validate form data before tokenization
	 */
	public validateFormData(formData: PaymentMethodFormData): boolean {
		const errors: Array<string> = [];

		if (formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD) {
			// Card number, expiration, and CVV are in secure fields — validate name only
			if (formData.nameOnCard.trim() === '') {
				errors.push('Name on card is required');
			}
		} else if (formData.paymentType === PaymentMethodTypeEnum.ACH) {
			// Routing number, account number, and name on account are all in
			// secure hosted fields (NMI requires checkname to be hosted for a
			// token to be generated) — validate account type only
			if (!['checking', 'savings'].includes(formData.accountType)) {
				errors.push('Valid account type (checking or savings) is required');
			}
		}

		// Common validation
		if (formData.zipCode.trim() === '') {
			errors.push('ZIP code is required');
		}

		if (errors.length > 0) {
			throw new PaymentGatewayError(
				`Validation failed: ${errors.join(', ')}`,
				'VALIDATION_ERROR',
			);
		}

		return true;
	}

	/**
	 * Check if service is initialized for a specific payment type
	 */
	public isInitialized(paymentType: PaymentContextType = 'CARD'): boolean {
		if (paymentType === 'CARD') {
			return this._isCardInitialized;
		}

		return this._isCheckInitialized;
	}

	/**
	 * Clean up resources for a specific payment type or all
	 */
	public cleanup(paymentType?: PaymentContextType): void {
		if (paymentType == null || paymentType === 'CARD') {
			this._isCardInitialized = false;
			this._detectedCardBrand = null;
			this._onCardBrandChange = null;
		}

		if (paymentType == null || paymentType === 'CHECK') {
			this._isCheckInitialized = false;
		}

		// Only clear config and shared state if cleaning up everything
		if (paymentType == null) {
			this._config = null;
			this._tokenResolve = null;
			this._tokenReject = null;
			this._currentPaymentType = null;
			this._lastTokenResponse = null;
			this._fieldValidity.clear();
			this._tokenRequestInFlight = false;
		}
	}

	// -------------------------------------------------------------------------
	// Private Helpers
	// -------------------------------------------------------------------------

	/**
	 * Clear any existing child content from a container element.
	 * Used to remove loading placeholders before Collect.JS injects its iframes.
	 */
	private _clearContainerContent(containerId: string): void {
		const container = document.getElementById(containerId);

		if (container != null) {
			container.innerHTML = '';
		}
	}

	/**
	 * Style iframe elements injected by Collect.JS to fill their container.
	 * Collect.JS injects iframes that don't inherit the container's dimensions,
	 * so we explicitly set them to fill the available space.
	 */
	private _stretchIframes(containerId: string): void {
		const container = document.getElementById(containerId);

		if (container == null) {
			return;
		}

		const iframes = container.querySelectorAll('iframe');

		iframes.forEach((iframe) => {
			iframe.style.width = '100%';
			iframe.style.height = '100%';
			iframe.style.border = 'none';
			iframe.style.display = 'block';
			iframe.style.background = 'transparent';
		});
	}

	/**
	 * Handle the Collect.JS callback with the tokenization response
	 */
	private _handleTokenResponse(response: CollectJSResponse): void {
		this._lastTokenResponse = response;

		// Extract card brand from response if available
		if (response.card?.type != null) {
			this._detectedCardBrand = this._mapCardBrand(response.card.type);
			this._onCardBrandChange?.(this._detectedCardBrand);
		}

		if (this._tokenResolve != null) {
			const dataDescriptor =
				this._currentPaymentType === 'CHECK'
					? 'NMI_ACH_PAYMENT_TOKEN'
					: 'NMI_PAYMENT_TOKEN';

			this._tokenResolve({
				dataDescriptor,
				dataValue: response.token,
			});

			this._tokenResolve = null;
			this._tokenReject = null;
			this._tokenRequestInFlight = false;
		}
	}

	/**
	 * Handle Collect.JS field validation events.
	 * Tracks per-field validity so a submit can fail fast with a specific
	 * message. When a token request is in flight and Collect.JS reports an
	 * invalid field, the token will never arrive — reject immediately
	 * instead of letting the request run into the 30s timeout.
	 */
	private _handleValidation(
		field: string,
		valid: boolean,
		message: string,
	): void {
		this._fieldValidity.set(field, { valid, message });

		if (!valid && this._tokenRequestInFlight && this._tokenReject != null) {
			const label = NmiFrontendService._fieldLabels[field] ?? field;

			this._tokenReject(
				new PaymentGatewayError(
					this._buildInvalidFieldsMessage([label]),
					'VALIDATION_ERROR',
				),
			);

			this._tokenResolve = null;
			this._tokenReject = null;
			this._tokenRequestInFlight = false;
		}
	}

	/**
	 * Handle the Collect.JS timeout — fired when no token was generated
	 * within timeoutDuration (e.g. the SDK failed to reach NMI).
	 */
	private _handleTokenTimeout(): void {
		if (this._tokenReject != null) {
			this._tokenReject(
				new PaymentGatewayError(
					'Payment form timed out. Please try again.',
					'TIMEOUT',
				),
			);
			this._tokenResolve = null;
			this._tokenReject = null;
		}

		this._tokenRequestInFlight = false;
	}

	/**
	 * Get configuration from backend
	 */
	private async _getConfiguration(): Promise<PaymentGatewayConfig> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: PaymentGatewayConfig;
				error?: string;
			}>('/payments/config/public');

			if (!response.success || response.data == null) {
				throw new Error(response.error ?? 'No configuration received');
			}

			return response.data;
		} catch (error) {
			throw new PaymentGatewayError(
				'Failed to fetch payment gateway configuration',
				'CONFIG_ERROR',
				error,
			);
		}
	}

	/**
	 * Load Collect.JS script dynamically with tokenization key
	 */
	private async _loadCollectJsScript(
		tokenizationKey: string,
		environment?: string,
	): Promise<void> {
		if (this._scriptLoaded && window.CollectJS != null) {
			return;
		}

		const scriptUrl = NmiFrontendService._getScriptUrl(environment);

		return new Promise((resolve, reject) => {
			// Check if script already exists (possibly from preloading)
			const existingScript = document.querySelector(
				`script[src="${scriptUrl}"]`,
			);

			// If CollectJS is already available, we're done
			if (window.CollectJS != null) {
				this._scriptLoaded = true;
				resolve();
				return;
			}

			// If script exists but CollectJS isn't ready yet, wait for it
			if (existingScript != null) {
				const checkCollectJS = (): void => {
					if (window.CollectJS != null) {
						this._scriptLoaded = true;
						resolve();
					} else {
						// Keep checking until CollectJS is available (max 10 seconds)
						setTimeout(checkCollectJS, 100);
					}
				};

				checkCollectJS();
				return;
			}

			// Script doesn't exist, create it
			const script = document.createElement('script');
			script.src = scriptUrl;
			script.setAttribute('data-tokenization-key', tokenizationKey);
			script.async = true;

			script.onload = (): void => {
				this._scriptLoaded = true;

				// Give CollectJS time to fully initialize
				setTimeout(() => {
					if (window.CollectJS != null) {
						resolve();
					} else {
						reject(
							new PaymentGatewayError(
								'Collect.JS did not initialize properly',
								'SCRIPT_INIT_ERROR',
							),
						);
					}
				}, 100);
			};

			script.onerror = (): void => {
				reject(
					new PaymentGatewayError(
						'Failed to load Collect.JS script',
						'SCRIPT_LOAD_ERROR',
					),
				);
			};

			document.head.appendChild(script);
		});
	}

	/**
	 * Map NMI card brand to internal format
	 */
	private _mapCardBrand(nmiBrand: string): string {
		const brandMap: Record<string, string> = {
			visa: 'Visa',
			mastercard: 'MasterCard',
			amex: 'Amex',
			discover: 'Discover',
			diners: 'Diners Club',
			jcb: 'JCB',
		};

		const lowerBrand = nmiBrand.toLowerCase();
		return brandMap[lowerBrand] ?? nmiBrand;
	}
}
